import { capitalizeFirstLetter, useIntl } from '@finn/ui-utils';
import { useCallback, useContext } from 'react';

import { VehicleContext } from '../../product-cards';
import { PDPTracking, useTrackingData } from '../tracking/PDPTracking';
import { EquipmentCollapseContent } from './EquipmentCollapseContent';

type Equipment = Record<string, string>;

export type EquipmentItemType = {
  key: string;
  items: {
    id: number;
    title: string;
    key: string;
  }[];
};

export const parseEquipmentData = (
  equipment: Equipment,
  delimiter: string
): EquipmentItemType[] => {
  return Object.entries(equipment)
    .filter((equ: string[]) => equ[1])
    .map((equ: string[], equIndex: number) => ({
      id: equIndex + 1,
      title: capitalizeFirstLetter(equ[0]),
      key: equ[0],
      items: equ[1].split(delimiter).map((item: string, itemIndex: number) => ({
        id: itemIndex + 1,
        title: item,
        key: item.toLowerCase(),
      })),
    }));
};

type EquipmentItem = {
  key: string;
  items: {
    id: number;
    title: string;
    key: string;
  }[];
};

export const Title = ({ title }: { title: string }) => (
  <p className="body-16-semibold">{title}</p>
);

export const EquipmentDetails = () => {
  const { vehicle } = useContext(VehicleContext);
  const i18n = useIntl();

  const equipmentItems = parseEquipmentData(
    vehicle?.equipment || {},
    vehicle?.equipment_delimiter || ', '
  );

  const packageItems = parseEquipmentData(
    vehicle?.equipment_packages || {},
    vehicle?.equipment_delimiter || ', '
  );

  const trackingData = useTrackingData();

  const trackEquipmentCollapseClicked = useCallback(
    (title: string, expanded: boolean) => {
      if (expanded) {
        PDPTracking.equipmentExpandClick({ value: title, ...trackingData });
      } else {
        PDPTracking.equipmentCollapseClick({ value: title, ...trackingData });
      }
    },
    [trackingData]
  );

  const renderItem = useCallback(
    (item: EquipmentItem) => {
      const title = i18n.formatMessage(`pdp.equipmentDetails.${item.key}`);

      return (
        <div key={item.key}>
          <p className="mb-4 mt-8 body-14-semibold">{title}</p>
          <EquipmentCollapseContent items={item.items} />
        </div>
      );
    },
    [i18n, trackEquipmentCollapseClicked]
  );

  const buildEquipmentPackagesContainer = (packages: EquipmentItem[]) => {
    return (
      <div className="flex flex-col">
        {packages.map((item) => (
          <div className="pb-8 last:pb-0" key={item.key}>
            <EquipmentCollapseContent title={item.key} items={item.items} />
          </div>
        ))}
      </div>
    );
  };

  const renderEquipmentPackages = useCallback(
    (packages: EquipmentItem[]) => {
      const title = i18n.formatMessage('pdp.equipmentDetails.packages');

      return (
        <>
          <p className="mb-4 mt-8 body-14-semibold">{title}</p>
          {buildEquipmentPackagesContainer(packages)}
        </>
      );
    },
    [i18n]
  );

  const hasAnyPackages = Object.keys(packageItems).length > 0;

  return (
    <>
      {equipmentItems.map(renderItem)}
      {hasAnyPackages && renderEquipmentPackages(packageItems)}
    </>
  );
};
